'use client';

import { useTranslations } from 'next-intl';
import styles from './serving-counter.module.css';

// Language-neutral glyphs hoisted so they don't trip i18next/no-literal-string.
const MINUS_GLYPH = '−';
const PLUS_GLYPH = '+';

interface ServingCounterProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  /** Compact sizing for inline placement in the recipe hero Serves meta cell.
   *  Smaller controls, no caption (the meta cell already supplies the "Serves" label). */
  compact?: boolean;
}

export function ServingCounter({
  value,
  min = 1,
  max = 20,
  onChange,
  compact = false,
}: ServingCounterProps) {
  const t = useTranslations();
  function decrement() {
    if (value > min) onChange(value - 1);
  }

  function increment() {
    if (value < max) onChange(value + 1);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const num = parseInt(e.target.value, 10);
    if (!isNaN(num) && num >= min && num <= max) {
      onChange(num);
    }
  }

  return (
    <div className={compact ? `${styles.wrapper} ${styles.compact}` : styles.wrapper}>
      <div className={styles.counter} role="group" aria-label={t('recipes.servingsLabel')}>
        <button
          type="button"
          className={styles.button}
          aria-label={t('mealPlan.decreaseServings')}
          disabled={value <= min}
          onClick={decrement}
        >
          {MINUS_GLYPH}
        </button>

        <div className={styles.inputWrapper}>
          {/* Accessible input — transparent overlay, keeps spinbutton semantics */}
          <input
            type="number"
            aria-label={t('settings.defaultServingsAria')}
            className={styles.input}
            value={value}
            min={min}
            max={max}
            onChange={handleChange}
          />
          {/* Visual display — remounts on value change to trigger crossfade */}
          <span key={value} className={styles.inputDisplay} aria-hidden="true">{value}</span>
        </div>

        <button
          type="button"
          className={styles.button}
          aria-label={t('mealPlan.increaseServings')}
          disabled={value >= max}
          onClick={increment}
        >
          {PLUS_GLYPH}
        </button>
      </div>

      {!compact && <span className={styles.label}>{t('settings.servingsLabel')}</span>}
    </div>
  );
}
